import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, Plus, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useProcessingRules } from '@/hooks/useProcessingRules';
import { useToast } from '@/hooks/use-toast';

interface AdvancedRule {
  id?: string;
  name: string;
  description: string;
  rule_type: 'validation' | 'extraction' | 'classification' | 'anomaly_detection';
  conditions: {
    field: string;
    operator: string;
    value: any;
    supplier_id?: string;
    context?: any;
  };
  actions: {
    type: 'flag' | 'auto_correct' | 'require_review' | 'reject';
    parameters?: any;
  };
  priority: number;
  is_active: boolean;
}

export const AdvancedValidationRules: React.FC = () => {
  const { toast } = useToast();
  const { rules, createRule, updateRule, deleteRule, isCreating, isUpdating, isDeleting } = useProcessingRules();
  const [selectedRuleType, setSelectedRuleType] = useState<string>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AdvancedRule | null>(null);
  const [formData, setFormData] = useState<AdvancedRule>({
    name: '',
    description: '',
    rule_type: 'validation',
    conditions: {
      field: '',
      operator: 'equals',
      value: ''
    },
    actions: {
      type: 'flag'
    },
    priority: 50,
    is_active: true
  });

  const validationRules = rules.filter(rule => rule.rule_type === 'validation');
  const anomalyRules = rules.filter(rule => rule.rule_type === 'anomaly_detection');
  const extractionRules = rules.filter(rule => rule.rule_type === 'extraction');
  const classificationRules = rules.filter(rule => rule.rule_type === 'classification');

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      rule_type: 'validation',
      conditions: {
        field: '',
        operator: 'equals',
        value: ''
      },
      actions: {
        type: 'flag'
      },
      priority: 50,
      is_active: true
    });
    setEditingRule(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingRule?.id) {
        await updateRule({ id: editingRule.id, updates: formData });
      } else {
        await createRule(formData);
      }
      resetForm();
      setIsCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to save rule:', error);
    }
  };

  const handleEdit = (rule: any) => {
    setFormData(rule);
    setEditingRule(rule);
    setIsCreateDialogOpen(true);
  };

  const handleDelete = async (ruleId: string) => {
    if (confirm('Are you sure you want to delete this rule?')) {
      await deleteRule(ruleId);
    }
  };

  const getSeverityColor = (priority: number) => {
    if (priority >= 80) return 'destructive';
    if (priority >= 60) return 'secondary';
    return 'outline';
  };

  const getRuleTypeColor = (type: string) => {
    switch (type) {
      case 'validation': return 'default';
      case 'anomaly_detection': return 'secondary';
      case 'consistency_check': return 'outline';
      case 'supplier_specific': return 'destructive';
      default: return 'outline';
    }
  };

  const renderRulesTable = (rulesList: any[], title: string) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{title}</span>
          <Badge variant="outline">{rulesList.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rulesList.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No {title.toLowerCase()} configured
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rule Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rulesList.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{rule.name}</div>
                      <div className="text-sm text-muted-foreground">{rule.description}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getRuleTypeColor(rule.rule_type)}>
                      {rule.rule_type.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <span className="font-medium">{rule.conditions.field}</span>
                      <span className="mx-2">{rule.conditions.operator}</span>
                      <span className="text-muted-foreground">{rule.conditions.value}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getSeverityColor(rule.priority)}>
                      {rule.priority}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {rule.is_active ? (
                      <div className="flex items-center gap-1 text-success">
                        <CheckCircle className="h-4 w-4" />
                        Active
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <XCircle className="h-4 w-4" />
                        Inactive
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(rule)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(rule.id)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Advanced Validation Rules</h2>
          <p className="text-muted-foreground">
            Configure sophisticated validation, anomaly detection, and supplier-specific rules
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingRule ? 'Edit Validation Rule' : 'Create New Validation Rule'}
              </DialogTitle>
              <DialogDescription>
                Configure advanced validation logic for bill processing
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Rule Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter rule name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rule_type">Rule Type</Label>
                  <Select
                    value={formData.rule_type}
                    onValueChange={(value: any) => setFormData({ ...formData, rule_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="validation">Field Validation</SelectItem>
                      <SelectItem value="anomaly_detection">Anomaly Detection</SelectItem>
                      <SelectItem value="extraction">Data Extraction</SelectItem>
                      <SelectItem value="classification">Document Classification</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this rule validates"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="field">Field</Label>
                  <Select
                    value={formData.conditions.field}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, field: value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="amount">Amount</SelectItem>
                      <SelectItem value="due_date">Due Date</SelectItem>
                      <SelectItem value="bill_date">Bill Date</SelectItem>
                      <SelectItem value="customer_name">Customer Name</SelectItem>
                      <SelectItem value="account_number">Account Number</SelectItem>
                      <SelectItem value="supplier_name">Supplier Name</SelectItem>
                      <SelectItem value="usage_amount">Usage Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="operator">Operator</Label>
                  <Select
                    value={formData.conditions.operator}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, operator: value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">Equals</SelectItem>
                      <SelectItem value="not_equals">Not Equals</SelectItem>
                      <SelectItem value="greater_than">Greater Than</SelectItem>
                      <SelectItem value="less_than">Less Than</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="regex">Regex Match</SelectItem>
                      <SelectItem value="range">In Range</SelectItem>
                      <SelectItem value="anomaly">Anomaly Detection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="value">Value</Label>
                  <Input
                    id="value"
                    value={formData.conditions.value}
                    onChange={(e) => setFormData({
                      ...formData,
                      conditions: { ...formData.conditions, value: e.target.value }
                    })}
                    placeholder="Enter value"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="action_type">Action Type</Label>
                  <Select
                    value={formData.actions.type}
                    onValueChange={(value: any) => setFormData({
                      ...formData,
                      actions: { ...formData.actions, type: value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flag">Flag for Review</SelectItem>
                      <SelectItem value="auto_correct">Auto Correct</SelectItem>
                      <SelectItem value="require_review">Require Manual Review</SelectItem>
                      <SelectItem value="reject">Reject Processing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority (1-100)</Label>
                  <Input
                    id="priority"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Rule is active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isCreating || isUpdating}>
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={selectedRuleType} onValueChange={setSelectedRuleType}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="all">All Rules</TabsTrigger>
          <TabsTrigger value="validation">Field Validation</TabsTrigger>
          <TabsTrigger value="anomaly">Anomaly Detection</TabsTrigger>
          <TabsTrigger value="consistency">Data Extraction</TabsTrigger>
          <TabsTrigger value="supplier">Classification</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-6">
          {renderRulesTable(rules, 'All Validation Rules')}
        </TabsContent>

        <TabsContent value="validation" className="space-y-6">
          {renderRulesTable(validationRules, 'Field Validation Rules')}
        </TabsContent>

        <TabsContent value="anomaly" className="space-y-6">
          {renderRulesTable(anomalyRules, 'Anomaly Detection Rules')}
        </TabsContent>

        <TabsContent value="consistency" className="space-y-6">
          {renderRulesTable(extractionRules, 'Data Extraction Rules')}
        </TabsContent>

        <TabsContent value="supplier" className="space-y-6">
          {renderRulesTable(classificationRules, 'Document Classification Rules')}
        </TabsContent>
      </Tabs>
    </div>
  );
};